import { resolveAssignmentScores } from '@/lib/formsScoring';
import { courseQuestionnaireScores } from '@/lib/courseQuestionnaireScores';
import type { StaffCourseQuestionnaire } from '@/lib/treatmentCourseStaffView';

export function questionnaireIsOnStep(row: StaffCourseQuestionnaire): boolean {
  const status = row.assignment_status;
  switch (status) {
    case 'cancelled':
    case 'expired':
      return false;
    case 'sent':
    case 'reminded':
    case 'completed':
    case 'in_progress':
    case 'opened':
      return true;
    default:
      return Boolean(
        row.assignment_id ||
          row.forms_assignment_id ||
          row.sent_at ||
          row.completed_at ||
          row.job_status === 'sent',
      );
  }
}

export function canUndoStepQuestionnaireSend(status: string | null | undefined): boolean {
  switch (status) {
    case 'sent':
    case 'reminded':
      return true;
    case 'completed':
    case 'expired':
    case 'cancelled':
    case 'in_progress':
    case 'opened':
      return false;
    default:
      return false;
  }
}

export function courseQuestionnairePrimaryScore(
  score: Record<string, unknown> | null | undefined,
): number | null {
  if (!score) return null;
  const resolved = resolveAssignmentScores([], {}, null, score);
  if (resolved && Number.isFinite(resolved.totalScore) && resolved.totalScore > 0) {
    return Math.round(resolved.totalScore);
  }
  const total =
    typeof score.total_score === 'number'
      ? score.total_score
      : typeof score.totalScore === 'number'
        ? score.totalScore
        : null;
  if (total != null && Number.isFinite(total)) return Math.round(total);
  const rows = courseQuestionnaireScores(score);
  return rows[0]?.value ?? null;
}

export const STEP_SEND_PREVIEW_LIMIT = 5;

export function sendableStepTemplates<T extends { id: string }>(
  templates: readonly T[],
  allowedIds: readonly string[] | null | undefined,
  onStepIds: ReadonlySet<string>,
): T[] {
  const pool =
    !allowedIds || allowedIds.length === 0
      ? templates
      : templates.filter((template) => allowedIds.includes(template.id));
  return pool.filter((template) => !onStepIds.has(template.id));
}

/** Tilladte in the clinic's order. Ids that are not in the library are skipped. */
export function templatesInAllowOrder<T extends { id: string }>(
  templates: readonly T[],
  allowedIds: readonly string[],
): T[] {
  const byId = new Map(templates.map((template) => [template.id, template]));
  const ordered: T[] = [];
  for (const id of allowedIds) {
    const template = byId.get(id);
    if (template) ordered.push(template);
  }
  return ordered;
}

/** Favourites (max 5), or 3–5 allowed forms when the user has none. Selected extras stay visible. */
export function previewStepSendItems<T extends { id: string }>(
  items: readonly T[],
  favoriteIds: readonly string[],
  selectedIds: readonly string[],
): T[] {
  const favoriteSet = new Set(favoriteIds);
  const favorites = items.filter((item) => favoriteSet.has(item.id)).slice(0, STEP_SEND_PREVIEW_LIMIT);
  const base = favorites.length > 0 ? favorites : items.slice(0, STEP_SEND_PREVIEW_LIMIT);
  const seen = new Set(base.map((item) => item.id));
  const extras = items.filter((item) => selectedIds.includes(item.id) && !seen.has(item.id));
  return extras.length === 0 ? [...base] : [...base, ...extras];
}

export function joinStepSendNames(names: readonly string[], language: string): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0] ?? '';
  const conjunction = language.startsWith('en') ? ' and ' : ' og ';
  if (names.length === 2) return `${names[0]}${conjunction}${names[1]}`;
  return `${names.slice(0, -1).join(', ')}${conjunction}${names[names.length - 1]}`;
}

export function stepSendAlreadyName(
  name: string,
  completed: boolean,
  filledLabel: string,
): string {
  return completed ? `${name} (${filledLabel})` : name;
}
